/**
 * The signed-in frame: the OutlierOS shell replica (packages/ui AppShell) configured for ViralWise
 * (docs/DASHBOARD_PARITY.md §3.1). RESEARCH · TOOLS · ADMIN groups, header capsule tabs, ⌘K, usage meter.
 */
import { plural } from '@vw/core/src/plural';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowUpDown, BarChart2, Bookmark, BookOpen, Compass, CreditCard, FolderHeart, Gift, Hash, HelpCircle, LogOut,
  Puzzle, Search, Settings, ShieldAlert, Sparkles, Users,
} from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { formatWiseScore } from '@vw/core/src/score';
import { AppShell, AvatarMenu, ErrorBoundary, NotificationBell, Toaster, UpgradeCard, UsageMeter, type BellItem, type NavGroup, type NavItem, type PaletteItem } from '@vw/ui';
import { api, type CollectionSummary, type SavedCreator } from './api';
import { BRAND_NAME, BRAND_TAGLINE, BrandLogo } from './brand';
import { navigate } from './router';
import { date } from './format';
import { useSession } from './session';

/** Which sidebar item / header tab a path belongs to. */
export function activeFor(path: string): { id: string; tab: string | null } {
  const m = /^\/c\/([^/]+)/.exec(path);
  if (m) return { id: `creator:${m[1]!.toLowerCase()}`, tab: 'explore' };
  if (path === '/' || path.startsWith('/feed')) return { id: 'feed', tab: 'feed' };
  if (path.startsWith('/explore')) return { id: 'explore', tab: 'explore' };
  if (path.startsWith('/library')) return { id: 'library', tab: 'library' };
  if (path.startsWith('/collections') || path.startsWith('/l/')) return { id: 'collections', tab: 'library' };
  for (const id of ['compare', 'settings', 'billing', 'earn', 'ai', 'welcome']) if (path.startsWith(`/${id}`)) return { id, tab: null };
  return { id: '', tab: null };
}

const PAGES: PaletteItem[] = [
  { id: 'p-feed', label: 'Your Feed', description: 'Creators you scanned and saved', category: 'Page', href: '/feed' },
  { id: 'p-explore', label: 'Creator Library', description: 'Public creators published by the team', category: 'Page', href: '/explore' },
  { id: 'p-library', label: 'Bookmarks & Hooks', description: 'Bookmarked reels and spoken hooks', category: 'Page', href: '/library' },
  { id: 'p-collections', label: 'Collections', description: 'Your collections and ones shared with you', category: 'Page', href: '/collections' },
  { id: 'p-compare', label: 'Compare Creators', description: 'Head-to-head viral benchmarks', category: 'Page', href: '/compare' },
  { id: 'p-billing', label: 'Billing & Plans', description: 'Plans, credits and invoices', category: 'Page', href: '/billing' },
  { id: 'p-settings', label: 'Settings & Extension', description: 'Connected browsers, AI apps and your account', category: 'Page', href: '/settings' },
  { id: 'p-ai', label: 'AI Connectors (MCP)', description: 'Use your research in Claude, ChatGPT and Cursor', category: 'Page', href: '/ai' },
  { id: 'p-welcome', label: 'Getting started', description: 'Four steps to your first Wise Score', category: 'Page', href: '/welcome' },
];

/** How often the header bell checks for new notifications (decision 84); paused while the tab is hidden. */
const BELL_POLL_MS = 60_000;

/** The header bell's data: latest notifications and the unread count, refreshed every minute and when opened. */
function useNotifications(refreshKey: number) {
  const [items, setItems] = useState<BellItem[] | null>(null);
  const [unread, setUnread] = useState(0);
  const load = () => api<{ items: BellItem[]; unread: number }>('/api/notifications').then((r) => { setItems(r.items); setUnread(r.unread); }, () => undefined);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, BELL_POLL_MS);
    return () => clearInterval(t);
  }, [refreshKey]);
  const read = (ids: string[] | 'all') => {
    const now = Date.now();
    setItems((xs) => xs && xs.map((n) => (ids === 'all' || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? now } : n)));
    setUnread((u) => (ids === 'all' ? 0 : Math.max(0, u - ids.length)));
    api<{ unread: number }>('/api/notifications/read', { method: 'POST', json: ids === 'all' ? { all: true } : { ids } }).then((r) => setUnread(r.unread), () => undefined);
  };
  return { items, unread, load, read };
}

export function Frame({ path, children }: { path: string; children: ReactNode }) {
  const { me, library, credits, refresh, refreshKey, settings } = useSession();
  // Creators without a current handle (rename conflict) can't be linked by handle: left out of nav and search.
  const saved = useMemo(() => (library ?? []).filter((c): c is SavedCreator & { handle: string } => !c.unavailable && !!c.handle), [library]);
  const [collections, setCollections] = useState<CollectionSummary[]>([]);
  useEffect(() => { api<{ collections: CollectionSummary[] }>('/api/collections').then((r) => setCollections(r.collections), () => undefined); }, [refreshKey]);
  // Tracked creators: the 6 most recently scanned in the library, with their peak (OutlierOS sidebar).
  const tracked = useMemo(() => [...saved].sort((a, b) => (b.lastScannedAt ?? 0) - (a.lastScannedAt ?? 0)).slice(0, 6), [saved]);
  const act = activeFor(path);
  const bell = useNotifications(refreshKey);
  const isAdmin = me.user.role === 'admin';
  const free = (me.plan.planSlug ?? 'free') === 'free';

  const groups: NavGroup[] = [
    {
      id: 'research', label: 'Research', items: [
        { id: 'search', label: 'Quick Search', icon: Search, kbd: '⌘K' },
        { id: 'feed', label: 'Your Feed', icon: BarChart2, href: '/feed', badge: saved.length || null },
        { id: 'explore', label: 'Creator Library', icon: Compass, href: '/explore' },
        ...(tracked.length ? [{
          id: 'tracked', label: 'Tracked Creators', icon: Sparkles,
          children: tracked.map((c) => ({ id: `creator:${c.handle}`, label: `@${c.handle}${c.peakOutlier !== null ? ` · ${formatWiseScore(c.peakOutlier)}` : ''}`, href: `/c/${c.handle}`, icon: Hash, ...(c.isStub ? { tag: 'Collab profile' } : {}) })),
        } satisfies NavItem] : []),
        { id: 'library', label: 'Bookmarks & Hooks', icon: Bookmark, href: '/library' },
        { id: 'collections', label: 'Collections', icon: FolderHeart, href: '/collections' },
        { id: 'compare', label: 'Compare Creators', icon: ArrowUpDown, href: '/compare' },
      ],
    },
    {
      id: 'tools', label: 'Tools', items: [
        { id: 'extension', label: 'Chrome Extension', icon: Puzzle, href: '/settings#extension' },
        { id: 'ai', label: 'AI Connectors (MCP)', icon: BookOpen, href: '/ai' },
        { id: 'earn', label: 'Earn & Invite', icon: Gift, href: '/earn' },
      ],
    },
    ...(isAdmin && me.links?.admin ? [{
      id: 'admin', label: 'Admin', items: [{ id: 'admin-home', label: 'Admin dashboard', icon: ShieldAlert, href: me.links.admin, external: true }],
    }] : []),
  ];
  const signOut = async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    navigate('/signin');
  };
  const bottom: NavItem[] = [
    { id: 'help', label: 'Help', icon: HelpCircle, href: me.links?.website ?? '/', external: true },
    { id: 'billing', label: 'Billing & Plans', icon: CreditCard, href: '/billing' },
    { id: 'settings', label: 'Settings', icon: Settings, href: '/settings', kbd: '⌘,' },
    { id: 'signout', label: 'Sign Out', icon: LogOut, onClick: signOut },
  ];
  const palette: PaletteItem[] = [
    ...saved.map((c) => ({ id: `c-${c.igPk}`, label: `@${c.handle}`, description: `${c.fullName ?? c.handle} • Wise Score analysis`, category: 'Creator', href: `/c/${c.handle}` })),
    ...collections.map((c) => ({ id: `col-${c.id}`, label: `${c.emoji ? `${c.emoji} ` : ''}${c.name}`, description: plural(c.itemCount, 'reel'), category: 'Collection', href: `/collections/${c.id}` })),
    ...PAGES,
  ];
  const footer = (
    <>
      {credits && <UsageMeter used={credits.used} limit={credits.allowance} href="/billing" onNavigate={navigate} upgradeHint={free} resetsOn={credits.resetsAt ? date(credits.resetsAt) : undefined} />}
      {free && <UpgradeCard planName={me.plan.planName ?? 'Free'} href="/billing" onNavigate={navigate} />}
    </>
  );

  return (
    <>
      <AppShell
        brand={{ name: BRAND_NAME, tagline: BRAND_TAGLINE, logo: <BrandLogo /> }}
        workspace={{ name: me.user.name?.split(' ')[0] ?? me.user.email.split('@')[0]!, plan: (me.plan.planName ?? 'Free').toUpperCase() }}
        groups={groups}
        bottom={bottom}
        footer={footer}
        tabs={[
          { id: 'feed', label: 'Your Feed', icon: BarChart2, href: '/feed' },
          { id: 'explore', label: 'Creator Library', icon: Users, href: '/explore', badge: null },
          { id: 'library', label: 'Bookmarks', icon: Bookmark, href: '/library' },
        ]}
        activeId={act.id}
        activeTab={act.tab}
        scrollKey={path}
        headerExtra={<>
          <NotificationBell items={bell.items} unread={bell.unread} onOpen={bell.load} onRead={(id) => bell.read([id])} onReadAll={() => bell.read('all')} onNavigate={navigate} />
          <AvatarMenu name={me.user.name ?? me.user.email.split('@')[0]!} email={me.user.email} plan={me.plan.planName ?? 'Free'} onNavigate={navigate} onSignOut={signOut} />
        </>}
        paletteItems={palette}
        onNavigate={(h) => (/^https?:/.test(h) ? window.open(h, '_blank', 'noopener') : navigate(h))}
        onRefresh={refresh}
        settingsHref="/settings"
      >
        {/* Maintenance banner (Admin → System, setting system.maintenanceMessage). */}
        {settings?.maintenanceMessage && <div role="status" className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">{settings.maintenanceMessage}</div>}
        <ErrorBoundary resetKey={path} supportEmail={BRAND.supportEmail}><div key={refreshKey}>{children}</div></ErrorBoundary>
      </AppShell>
      <Toaster />
    </>
  );
}
